import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Select, SelectOption, Stack, Tag } from '@pypestream/design-system';
import { observer } from 'mobx-react-lite';

import { SelectPlaceholder } from './select-placeholder';
import { TableRow, tableStore } from '../../stores/table-store';

interface NodeBehaviorsCellProps {
  // value: TableRow['behaviors'];
  onEdit?: (value: TableRow['behaviors']) => void;
  // isEditing?: boolean;
  rowId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columnId?: string | number | symbol | any;
  // index?: number;
}

const behaviorOptionsMap = {
  disable_input: 'Disable Free Text',
  inline: 'Inline Webview',
  multiple_choice: 'Multiple Choice Listpicker',
  xfer_to_agent: 'Transfer to Live Agent',
  voice_enable_barge_in: 'Allow Users to Interrupt Voice',
};

const getBehaviorOptionLabel = (value: string) => {
  return behaviorOptionsMap[value as keyof typeof behaviorOptionsMap];
};

export const NodeBehaviorsMultiSelect: React.FC<NodeBehaviorsCellProps> =
  observer(({ onEdit, rowId, columnId }) => {
    const [isSelectRendered, setIsSelectRendered] = useState(false);
    const index = tableStore.getRowIndex(rowId);
    const selectRef = useRef<React.ComponentRef<typeof Select>>(null);
    const initialValue = tableStore.getValue(
      rowId,
      columnId
    ) as TableRow['behaviors'];

    useEffect(() => {
      if (isSelectRendered && selectRef.current) {
        // Use a longer delay to ensure the component is fully rendered
        setTimeout(() => {
          if (
            selectRef.current &&
            typeof selectRef.current.show === 'function'
          ) {
            selectRef.current.show();
            if (selectRef.current.focus) {
              selectRef.current.focus();
            }
          }
        }, 50);
      }
    }, [isSelectRendered]);

    const handleOnEdit = useCallback(
      (values: TableRow['behaviors'], closestCell: HTMLElement | null) => {
        onEdit?.(values);

        if (closestCell) {
          closestCell.focus();
          setTimeout(() => {
            tableStore.isCellBeingEdited = false;
          }, 100);
        }
      },
      [onEdit]
    );

    if (tableStore.isEditing()) {
      if (!isSelectRendered) {
        return (
          <SelectPlaceholder
            testId={`c-editor-table--${columnId}-${index}-select-placeholder`}
            onOpen={() => setIsSelectRendered(true)}
            ariaLabel="Open behaviors selection"
            style={{ margin: '-4px 0' }}
          >
            <Stack gutter="2xsmall" direction="row">
              <div className="c-editor-table-multiselect-placeholder-tags">
                {initialValue?.map((value, tagIndex) => {
                  if (tagIndex > 4) {
                    return null;
                  }
                  const isConsolidationTag = tagIndex === 4;

                  return (
                    <Tag
                      key={value}
                      size="small"
                      removable={!isConsolidationTag}
                      newVariant="filled"
                      variant="default"
                      minimal
                      onRemove={(e) => {
                        handleOnEdit(
                          initialValue?.filter(
                            (v) => v !== value
                          ) as TableRow['behaviors'],
                          (e.target as HTMLElement)?.closest?.('td')
                        );
                      }}
                      tabIndex={0}
                    >
                      {(isConsolidationTag && (
                        <span>+ {initialValue.length - 4}</span>
                      )) ||
                        getBehaviorOptionLabel(value)}
                    </Tag>
                  );
                })}
              </div>
            </Stack>
          </SelectPlaceholder>
        );
      }

      return (
        <Select
          ref={selectRef}
          test-id={`c-editor-table--${columnId}-${index}-select`}
          value={initialValue}
          multiple
          maxOptionsVisible={4}
          size="small"
          onclose={(_ev) => {
            setTimeout(() => {
              tableStore.isCellBeingEdited = false;
            }, 100);

            setTimeout(() => {
              if (selectRef.current?.open) return;

              if (isSelectRendered) {
                setIsSelectRendered(false); // Reset to fake select after closing
              }
            }, 2000);
          }}
          onmouseleave={() => {
            if (selectRef.current?.open) return;

            setTimeout(() => {
              setIsSelectRendered(false); // Reset to fake select after closing
            }, 500);
          }}
          variant="outlined"
          onChange={(e) => {
            handleOnEdit(
              e.target.value as TableRow['behaviors'],
              e.target.closest('td')
            );
          }}
        >
          {Object.keys(behaviorOptionsMap).map((value) => (
            <SelectOption key={value} value={value}>
              {getBehaviorOptionLabel(value)}
            </SelectOption>
          ))}
        </Select>
      );
    }

    return (
      <div
        className="c-editor-table-multiselect-placeholder-tags"
        style={{ margin: '-4px 0' }}
      >
        <>
          {initialValue &&
            initialValue?.map((value) => {
              return (
                <div className="c-editor-table__tag" key={value}>
                  {getBehaviorOptionLabel(value)}
                </div>
              );
            })}

          {!initialValue && <div className="c-editor-table__tag">None</div>}
        </>
      </div>
    );
  });
